import { useState } from 'react';
import { FiCheckCircle, FiAlertTriangle, FiClock, FiTrendingUp, FiInbox, FiEye, FiDownload, FiUploadCloud, FiFileText } from 'react-icons/fi';
import { TbPencil } from 'react-icons/tb';
import AppLayout from '../components/layout/AppLayout';
import PageHeader from '../components/common/PageHeader';
import Card from '../components/common/Card';
import StatCard from '../components/common/StatCard';
import SegmentedControl from '../components/common/SegmentedControl';
import EmptyState from '../components/common/EmptyState';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Chip from '../components/common/Chip';
import OtpInput from '../components/common/OtpInput';
import NavGlyph, { ICON_FOR_EMOJI } from '../components/common/NavGlyph';
import { Field, TextField, Select, DateField } from '../components/common/FormControls';
import { SkeletonBar, SkeletonCard } from '../components/common/Skeleton';
import { useUI } from '../context/UIContext';
import { Icon, IconTile, ICONS } from '../components/common/Icon';

/**
 * DesignSystemPage — the living reference for the New UI design language.
 * Dev-only route (/design-system). Single source of truth for tokens +
 * primitives so future work stays on-language instead of drifting inline.
 */

function Section({ title, subtitle, children }) {
  return (
    <div className="mb-9">
      <div className="text-[17px] font-bold mb-1" style={{ fontFamily: 'var(--font-serif)', color: '#08111f' }}>{title}</div>
      {subtitle && <div className="text-[12px] text-[#7a7060] mb-3">{subtitle}</div>}
      <div className={subtitle ? '' : 'mt-3'}>{children}</div>
    </div>
  );
}

function Swatch({ name, varName }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="h-14 rounded-[12px] border" style={{ background: `var(${varName})`, borderColor: 'rgba(226,217,200,0.7)' }} />
      <div className="text-[11px] font-semibold text-[#08111f]">{name}</div>
      <div className="text-[10px] text-[#7a7060]" style={{ fontFamily: 'var(--font-mono)' }}>{varName}</div>
    </div>
  );
}

const BRAND = [
  ['Ink', '--ink'], ['Paper', '--paper'], ['Teal', '--teal'], ['Teal mid', '--teal-mid'],
  ['Gold', '--gold'], ['Surface 2', '--surface2'], ['Border', '--border'], ['Muted', '--muted'],
];
const SEMANTIC = [['Success', '--success'], ['Warning', '--warning'], ['Danger', '--danger'], ['Info', '--info']];
const RADII = [['sm 8', '--r-sm'], ['md 10', '--r-md'], ['lg 12', '--r-lg'], ['xl 16', '--r-xl'], ['2xl 20', '--r-2xl']];
const SHADOWS = [['sh-1 card', '--sh-1'], ['sh-2 raised', '--sh-2'], ['sh-3 modal', '--sh-3']];
const SPACING = [['sp-1', 4], ['sp-2', 8], ['sp-3', 12], ['sp-4', 16], ['sp-5', 20], ['sp-6', 24], ['sp-7', 32], ['sp-8', 48]];
const BUTTON_VARIANTS = ['primary', 'gold', 'ink', 'outline', 'ghost', 'danger', 'success'];

// Labels for the sidebar/nav glyph set — keyed by the emoji NavGlyph maps from.
const NAV_GLYPH_LABELS = {
  '🏠': 'dashboard', '🏢': 'companies', '🔔': 'alerts', '📊': 'reporting',
  '🏗': 'portfolio', '🤝': 'llp / collab', '📈': 'public co', '👤': 'opc',
  '🌱': 'ngo', '🤖': 'ai agent', '📋': 'minutes', '📜': 'resolutions',
  '🗂': 'doc library', '🧾': 'report gen', '📂': 'my documents', '👥': 'collaborators',
  '📅': 'calendar', '📑': 'mca forms', '🏬': 'marketplace', '💰': 'valuation',
  '🔍': 'due diligence', '🔒': 'privacy', '🛡': 'dp controls', '⚙': 'settings',
  '📦': 'orders', '🔧': 'services', '🔐': 'roles', '🚩': 'flag inbox',
  '🎧': 'support', '🎫': 'tickets',
};

export default function DesignSystemPage() {
  const [seg, setSeg] = useState('all');
  const [demoText, setDemoText] = useState('');
  const [demoSelect, setDemoSelect] = useState('');
  const [demoDate, setDemoDate] = useState('');
  const [demoOtp, setDemoOtp] = useState('');
  const [showModal, setShowModal] = useState(false);
  const { showToast } = useUI();

  return (
    <AppLayout pageTitle="Design System">
      <PageHeader
        title="Design System"
        subtitle="The living reference for tokens and primitives. Build on these — never hardcode a value that has a token."
      />

      {/* Colours */}
      <Section title="Brand palette" subtitle="Muted, earthy, low-saturation — trust over digital brightness.">
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {BRAND.map(([n, v]) => <Swatch key={v} name={n} varName={v} />)}
        </div>
      </Section>

      <Section title="Semantic intent" subtitle="Reference intent, not raw hex — these map onto the brand hues.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {SEMANTIC.map(([n, v]) => <Swatch key={v} name={n} varName={v} />)}
        </div>
      </Section>

      {/* Typography */}
      <Section title="Typography" subtitle="Cormorant Garamond for headings, Outfit for UI, JetBrains Mono for code.">
        <Card className="flex flex-col gap-3">
          <div style={{ fontFamily: 'var(--font-serif)' }} className="text-[32px] font-bold leading-none text-[#08111f]">Cormorant Garamond — headings</div>
          <div style={{ fontFamily: 'var(--font-sans)' }} className="text-[15px] text-[#08111f]">Outfit — body & UI text. The quick brown fox jumps over the lazy dog.</div>
          <div style={{ fontFamily: 'var(--font-mono)' }} className="text-[13px] text-[#7a7060]">JetBrains Mono — AOC-4 · MGT-7 · ₹100/day</div>
        </Card>
      </Section>

      {/* Spacing */}
      <Section title="Spacing scale" subtitle="4px grid.">
        <div className="flex items-end gap-3 flex-wrap">
          {SPACING.map(([n, px]) => (
            <div key={n} className="flex flex-col items-center gap-1.5">
              <div style={{ width: px, height: px, background: 'var(--teal)', borderRadius: 4 }} />
              <div className="text-[10px] text-[#7a7060]" style={{ fontFamily: 'var(--font-mono)' }}>{n}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Radius + shadow */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Radius">
          <div className="flex items-center gap-3 flex-wrap">
            {RADII.map(([n, v]) => (
              <div key={v} className="flex flex-col items-center gap-1.5">
                <div className="w-14 h-14 bg-white border" style={{ borderRadius: `var(${v})`, borderColor: 'var(--border)' }} />
                <div className="text-[10px] text-[#7a7060]" style={{ fontFamily: 'var(--font-mono)' }}>{n}</div>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Elevation">
          <div className="flex items-center gap-4 flex-wrap">
            {SHADOWS.map(([n, v]) => (
              <div key={v} className="flex flex-col items-center gap-1.5">
                <div className="w-16 h-14 bg-white rounded-[12px]" style={{ boxShadow: `var(${v})` }} />
                <div className="text-[10px] text-[#7a7060]" style={{ fontFamily: 'var(--font-mono)' }}>{n}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Buttons */}
      <Section title="Buttons" subtitle="Button.jsx — variants × sizes.">
        <Card className="flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {BUTTON_VARIANTS.map((v) => <Button key={v} variant={v} size="sm">{v}</Button>)}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {['xs', 'sm', 'md', 'lg'].map((s) => <Button key={s} variant="primary" size={s}>Size {s}</Button>)}
          </div>
        </Card>
      </Section>

      {/* Header-action buttons */}
      <Section
        title="Header-action buttons"
        subtitle="cs-btn--header-action — 32px squared icon buttons used in modal headers (edit / AI / export). Cohesive with the notification bell. Add .active for a toggled-on state (e.g. edit unlocked)."
      >
        <Card className="flex items-center gap-2 flex-wrap">
          <Button size="sm" iconOnly className="cs-btn--header-action" title="View" leftIcon={<FiEye size={16} />} />
          <Button size="sm" iconOnly className="cs-btn--header-action" title="Enable edit" leftIcon={<TbPencil size={16} />} />
          <Button size="sm" iconOnly className="cs-btn--header-action active" title="Editing (active)" leftIcon={<TbPencil size={16} />} />
          <Button size="sm" iconOnly className="cs-btn--header-action" title="Export" leftIcon={<FiDownload size={16} />} />
          <span className="text-[11px] text-[#7a7060] ml-2">default · default · <span className="text-[#145f5f] font-semibold">.active</span> · default</span>
        </Card>
      </Section>

      {/* Modal */}
      <Section
        title="Modal"
        subtitle="Modal.jsx — teal 3px top-rule, cream header/footer bands, softer blurred backdrop, bordered close button. Header uses Cormorant Garamond."
      >
        <Card>
          <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>Open modal</Button>
        </Card>
      </Section>

      {/* Chips */}
      <Section title="Chips" subtitle="Chip.jsx — status / type / urgency pills.">
        <div className="flex items-center gap-2 flex-wrap">
          <Chip tone="#7a7060">Neutral</Chip>
          <Chip bg="#e9f0e4" color="#4a6b3f">Compliant</Chip>
          <Chip bg="#f3ebd7" color="#8a6a1f">Due soon</Chip>
          <Chip bg="#f6e9e6" color="#9a564a">Overdue</Chip>
          <Chip bg="#e8ecf3" color="#3a4a66">Info</Chip>
        </div>
      </Section>

      {/* Surface cards */}
      <Section
        title="Surface card"
        subtitle="cs-resolution-card — the standard interactive tile (resolutions, doc library, collaborators, reports). Hairline border, faint shadow, lifts 2px on hover."
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {['Board Resolution', 'Special Resolution', 'Ordinary Resolution'].map((t) => (
            <div key={t} className="cs-resolution-card group rounded-[14px] px-4 py-3.5 cursor-pointer flex items-center gap-3">
              <IconTile name="legal" size="sm" tone="#145f5f" />
              <div>
                <div className="text-[13px] font-semibold text-[#08111f]">{t}</div>
                <div className="text-[11px] text-[#7a7060]">Hover to see the lift</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Upload dropzone */}
      <Section
        title="Upload dropzone"
        subtitle="cs-upload-dropzone — frosted, dashed drop target for supporting-document attachment. Border turns teal on hover."
      >
        <div className="cs-upload-dropzone rounded-[14px] px-6 py-8 flex flex-col items-center justify-center text-center gap-1.5 cursor-pointer max-w-[420px]">
          <FiUploadCloud size={26} className="text-[#145f5f]" />
          <div className="text-[13px] font-semibold text-[#08111f]">Drop a file or click to browse</div>
          <div className="text-[11px] text-[#7a7060] flex items-center gap-1">
            <FiFileText size={12} /> PDF · JPG · PNG · DOCX · up to 2 files
          </div>
        </div>
      </Section>

      {/* Scrollbars */}
      <Section
        title="Premium scrollbar"
        subtitle="cs-premium-scrollbar — ultra-thin 4px rail for horizontal docks & pill selectors. no-scrollbar hides the rail entirely. Scroll the strip →"
      >
        <div className="flex items-center gap-2 overflow-x-auto pb-2 cs-premium-scrollbar">
          {Array.from({ length: 16 }, (_, i) => (
            <span
              key={i}
              className="flex-shrink-0 px-3.5 py-1.5 rounded-full text-[12px] font-medium border"
              style={{ background: '#faf7f2', borderColor: '#e2d9c8', color: '#5a5145' }}
            >
              Filter {i + 1}
            </span>
          ))}
        </div>
      </Section>

      {/* Form controls */}
      <Section title="Form controls" subtitle="FormControls.jsx — TextField, Select (searchable portal), DateField. Teal focus ring on all.">
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Text field" required>
              <TextField value={demoText} onChange={(e) => setDemoText(e.target.value)} placeholder="COMPANY NAME PVT LTD" />
            </Field>
            <Field label="Select">
              <Select
                value={demoSelect}
                onChange={setDemoSelect}
                options={['Private Limited', 'LLP', 'Public Limited', 'OPC', 'Section 8 / NGO', 'NBFC', 'Producer Company']}
                placeholder="— Entity type —"
              />
            </Field>
            <Field label="Date field">
              <DateField value={demoDate} onChange={setDemoDate} placeholder="Select AGM date" />
            </Field>
          </div>
        </Card>
      </Section>

      {/* OTP input */}
      <Section title="OTP input" subtitle="OtpInput.jsx — one real input over six slots: native paste, iOS/Android one-time-code autofill, numeric keypad, auto-submit on last digit.">
        <Card className="max-w-[380px]">
          <OtpInput length={6} value={demoOtp} onChange={setDemoOtp} onComplete={(c) => showToast(`Code entered: ${c}`, 'success')} />
        </Card>
      </Section>

      {/* Toasts */}
      <Section title="Toasts" subtitle="UIContext showToast — bottom-right, dark surface, tone dot.">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="primary" size="sm" onClick={() => showToast('Company saved successfully', 'success')}>Success</Button>
          <Button variant="danger" size="sm" onClick={() => showToast('Could not reach MCA — try again', 'error')}>Error</Button>
          <Button variant="warning" size="sm" onClick={() => showToast('AGM date missing — deadlines are estimates', 'warning')}>Warning</Button>
          <Button variant="outline" size="sm" onClick={() => showToast('Reminder scheduled for tomorrow', 'info')}>Info</Button>
        </div>
      </Section>

      {/* Skeletons */}
      <Section title="Loading states" subtitle="Skeleton.jsx — always skeletons, never spinners-in-a-void.">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card><SkeletonBar w="40%" h={14} className="mb-3" /><SkeletonBar className="mb-2" /><SkeletonBar w="80%" className="mb-2" /><SkeletonBar w="60%" /></Card>
          <SkeletonCard rows={3} />
        </div>
      </Section>

      {/* StatCard + SegmentedControl */}
      <Section title="StatCard" subtitle="KPI tile.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Compliant" value={12} sub="80% of portfolio" tone="#4a6b3f" icon={FiCheckCircle} />
          <StatCard label="Due This Week" value={3} sub="Within 7 days" tone="#8a6a1f" icon={FiClock} />
          <StatCard label="Overdue" value={2} sub="Immediate" tone="#9a564a" icon={FiAlertTriangle} />
          <StatCard label="Exposure" value="₹1.2L" sub="If unaddressed" tone="#9a564a" icon={FiTrendingUp} compact />
        </div>
      </Section>

      <Section title="SegmentedControl">
        <SegmentedControl
          options={[{ key: 'all', label: 'All', count: 17 }, { key: 'overdue', label: 'Overdue', count: 2 }, { key: 'week', label: 'Week', count: 3 }]}
          value={seg}
          onChange={setSeg}
        />
      </Section>

      {/* EmptyState */}
      <Section title="EmptyState">
        <EmptyState icon={FiInbox} title="Nothing here yet" subtitle="Standardized empty block with an optional call to action." action={<Button variant="primary" size="sm">Primary action</Button>} />
      </Section>

      {/* Icons */}
      <Section title="Icon system" subtitle="Semantic line icons (Icon / IconTile). No emoji as UI chrome.">
        <Card>
          <div className="grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-12 gap-3">
            {Object.keys(ICONS).slice(0, 36).map((name) => (
              <div key={name} className="flex flex-col items-center gap-1.5 text-center">
                <IconTile name={name} size="md" tone="#145f5f" />
                <div className="text-[9px] text-[#7a7060] truncate w-full">{name}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-[#7a7060] flex items-center gap-1.5">
            <Icon name="info" size={13} style={{ color: '#3a4a66' }} /> {Object.keys(ICONS).length} semantic names available.
          </div>
        </Card>
      </Section>

      {/* IconTile sizes */}
      <Section title="IconTile sizes" subtitle="xs 24 · sm 28 · md 38 · lg 46 · xl 56 — tinted 10% wash of any brand tone.">
        <Card className="flex items-end gap-4 flex-wrap">
          {['xs', 'sm', 'md', 'lg', 'xl'].map((s) => (
            <div key={s} className="flex flex-col items-center gap-1.5">
              <IconTile name="legal" size={s} tone="#145f5f" />
              <div className="text-[10px] text-[#7a7060]">{s}</div>
            </div>
          ))}
          <div className="w-px h-10 mx-2" style={{ background: 'var(--border)' }} />
          {['#145f5f', '#b8973a', '#4a6b3f', '#9a564a', '#3a4a66', 'neutral'].map((t) => (
            <div key={t} className="flex flex-col items-center gap-1.5">
              <IconTile name="shield" size="md" tone={t} />
              <div className="text-[10px] text-[#7a7060]">{t === 'neutral' ? 'neutral' : t}</div>
            </div>
          ))}
        </Card>
      </Section>

      {/* Sidebar / nav glyphs */}
      <Section
        title="Sidebar & nav icons"
        subtitle="NavGlyph.jsx — Remix filled glyphs used by the sidebar, spotlight search and quick actions. Data keeps emoji keys; NavGlyph maps them at render."
      >
        <Card>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-10 gap-3">
            {Object.keys(ICON_FOR_EMOJI).map((emoji) => (
              <div key={emoji} className="flex flex-col items-center gap-1.5 text-center">
                <span
                  className="inline-grid place-items-center"
                  style={{ width: 38, height: 38, borderRadius: 'var(--r-md)', background: '#0d2a3a', color: '#d4b05a' }}
                >
                  <NavGlyph icon={emoji} size={17} />
                </span>
                <div className="text-[9px] text-[#7a7060] truncate w-full">{NAV_GLYPH_LABELS[emoji] || emoji}</div>
              </div>
            ))}
          </div>
        </Card>
      </Section>

      {/* Live Modal demo — teal top-rule, cream bands, bordered close */}
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Restyled modal"
        headerActions={
          <>
            <Button size="sm" iconOnly className="cs-btn--header-action" title="Enable edit" leftIcon={<TbPencil size={16} />} />
            <Button size="sm" iconOnly className="cs-btn--header-action" title="Export" leftIcon={<FiDownload size={16} />} />
          </>
        }
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={() => setShowModal(false)}>Confirm</Button>
          </>
        }
      >
        <div className="px-6 py-6 text-[13px] text-[#5a5145] leading-relaxed">
          Teal 3px top-rule, cream header/footer bands over a softly blurred backdrop.
          Header actions sit inline with the bordered close button. This is the same
          Modal primitive every dialog in the app builds on.
        </div>
      </Modal>
    </AppLayout>
  );
}
